import _ from 'lodash';
import { SelectionAreaWrapper } from '@/shared/ui';
import { ID_DIV_STRIP_RHYTHM_EVENT } from '@/entities/event';
import { bookmarkOffsetPreviewStrip } from '@/entities/strip';
import BookmarkPreviewStrip from './bookmarkPreviewStrip';
import useStripBookmarks from './useStripBookmarks';

export type StripBookmarksProps = {
  activeButton: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  startReport: any;
  ecgDataMap: any;
};

/**
 * Renders the scrollable list of ECG bookmark thumbnails for the current page. Wraps BookmarkPreviewStrip items in a drag-selection area seeded with the active strip id, marking an item selected only when the selection area reports it and useStripBookmarks confirms a selection exists.
 *
 * @param props - Strip list inputs including the active event-type button, ECG data map, study and profile ids, timezone offset and report start.
 * @returns The selectable bookmark strip list.
 */
const StripBookmarks = ({
  activeButton,
  studyId,
  profileId,
  timezoneOffset: timezoneOffsetProp,
  startReport: startReportProp,
  ecgDataMap: ecgDataMapProp,
}: StripBookmarksProps) => {
  const {
    selectionAreaWrapperRef,
    ecgBookmarksInPage,
    selectedStrip,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
    timezoneOffset,
    startReport,
    ecgDataMap,
    setSelectedBookmark,
    handleClickSelectBookmark,
  } = useStripBookmarks({
    activeButton,
    studyId,
    profileId,
    timezoneOffset: timezoneOffsetProp,
    startReport: startReportProp,
    ecgDataMap: ecgDataMapProp,
  });

  return (
    <SelectionAreaWrapper
      ref={selectionAreaWrapperRef}
      id={ID_DIV_STRIP_RHYTHM_EVENT}
      className="strips-container --min-width-strip-event"
      selectables=".single-strip-event"
      handleSetSelected={setSelectedBookmark}
      defaultSelectedId={selectedStrip?.id as string}
      isNoData={ecgBookmarksInPage?.length === 0}
      // this view never tracked a loading state; SelectionAreaWrapper requires the prop
      isLoading={false}
    >
      {(selected) => (
        <div className="strips-img-container">
          {_.map(ecgBookmarksInPage, (bookmark, index) => (
            <BookmarkPreviewStrip
              key={bookmark.id}
              index={index}
              id={`beat-${bookmark.id}`}
              isActive={bookmark.id === selectedStrip?.id}
              isSelected={selected.has(`beat-${bookmark.id}`) && isHasSelected}
              bookmark={bookmark}
              ecgDataMap={ecgDataMap}
              highPass={highPassThumbnail}
              gain={gainThumbnail}
              channels={channelsThumbnail}
              width={sizeThumbnailConfig.width}
              height={sizeThumbnailConfig.height}
              customOffset={bookmarkOffsetPreviewStrip}
              timezoneOffset={timezoneOffset}
              startReport={startReport}
              onClickSelectEvent={handleClickSelectBookmark}
            />
          ))}
        </div>
      )}
    </SelectionAreaWrapper>
  );
};

export default StripBookmarks;
